import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  ShoppingCart,
  Heart,
  Share2,
  Phone,
  Mail,
  MapPin,
  Star,
  Award,
  Users,
  Shield,
} from "lucide-react";
import kenteHero from "@/assets/kente-hero.jpg";

interface StoreData {
  name: string;
  description: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  items: any[];
  isPublished: boolean;
  storeUrl: string;
  contactInfo?: {
    email: string;
    phone: string;
    location: string;
    businessHours: {
      weekdays: string;
      saturday: string;
      sunday: string;
    };
  };
}

interface StorePreviewProps {
  storeData: StoreData;
}

const StorePreview = ({ storeData }: StorePreviewProps) => {
  const mockItems =
    storeData.items.length > 0
      ? storeData.items
      : [
          {
            id: "1",
            name: "Traditional Kente Sash",
            description:
              "Handwoven traditional Kente sash with authentic patterns",
            price: 75,
            category: "Kente Sash",
            images: [],
            isPublished: true,
            stock: 10,
          },
          {
            id: "2",
            name: "Modern Kente Bag",
            description: "Contemporary Kente design bag perfect for daily use",
            price: 45,
            category: "Kente Bag",
            images: [],
            isPublished: true,
            stock: 5,
          },
          {
            id: "3",
            name: "Royal Kente Smock",
            description: "Elegant Kente smock for special occasions",
            price: 120,
            category: "Kente Smock",
            images: [],
            isPublished: true,
            stock: 3,
          },
        ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">Store Preview</h3>
          <p className="text-muted-foreground">
            Preview how your store will look to customers
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share Preview
          </Button>
          <Button size="sm">
            <Eye className="h-4 w-4 mr-2" />
            View Live Store
          </Button>
        </div>
      </div>

      {/* Store Preview */}
      <div className="border rounded-lg overflow-hidden bg-white shadow-lg">
        {/* Header */}
        <div
          className="p-4 border-b"
          style={{ backgroundColor: storeData.primaryColor, color: "white" }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {storeData.logo && (
                <img
                  src={storeData.logo}
                  alt="Store Logo"
                  className="h-10 w-10 object-contain bg-white rounded p-1"
                />
              )}
              <div>
                <h1 className="text-xl font-bold">
                  {storeData.name || "My Kente Store"}
                </h1>
                <p className="text-sm opacity-90">
                  {storeData.description ||
                    "Authentic Kente designs crafted with love"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white bg-white/10 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105 shadow-md backdrop-blur-sm"
                onClick={() => {
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Phone className="h-4 w-4 mr-2" />
                Contact
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white bg-white/10 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105 shadow-md backdrop-blur-sm"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div
          className="flex gap-8 px-6 py-4 border-b shadow-sm"
          style={{ backgroundColor: storeData.secondaryColor, color: "white" }}
        >
          <a
            href="#home"
            className="hover:underline font-medium transition-all duration-200 hover:scale-105"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("home")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Home
          </a>
          <a
            href="#about"
            className="hover:underline transition-all duration-200 hover:scale-105"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("about")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            About
          </a>
          <a
            href="#store"
            className="hover:underline transition-all duration-200 hover:scale-105"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("store")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Store
          </a>
          <a
            href="#contact"
            className="hover:underline transition-all duration-200 hover:scale-105"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Contact
          </a>
        </div>

        {/* Hero Section */}
        <div className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          {/* Background with gradient overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={kenteHero}
              alt="Traditional Kente cloth background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="mb-6">
                {storeData.logo && (
                  <img
                    src={storeData.logo}
                    alt="Store Logo"
                    className="h-16 w-16 object-contain bg-white rounded-full p-2 mx-auto mb-6"
                  />
                )}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Welcome to {storeData.name || "My Kente Store"}
              </h2>
              <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
                {storeData.description ||
                  "Discover authentic Kente designs that celebrate our rich cultural heritage. Each piece is carefully crafted with traditional techniques passed down through generations."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                  onClick={() => {
                    document
                      .getElementById("store")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <ShoppingCart className="h-6 w-6 mr-2" />
                  Shop Our Collection
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white bg-white/10 text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl backdrop-blur-sm"
                  onClick={() => {
                    document
                      .getElementById("about")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <Award className="h-6 w-6 mr-2" />
                  Learn Our Story
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Products */}
        <div className="p-8 bg-gray-50">
          <div className="text-center mb-8">
            <h3
              className="text-2xl font-bold mb-2"
              style={{ color: storeData.primaryColor }}
            >
              Featured Products
            </h3>
            <p className="text-muted-foreground">
              Handpicked Kente designs for every occasion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockItems.slice(0, 6).map((item) => (
              <Card
                key={item.id}
                className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
              >
                <div className="aspect-square bg-gray-100 relative group">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div
                          className="w-20 h-20 mx-auto mb-3 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: storeData.accentColor }}
                        >
                          <span className="text-3xl">🧵</span>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">
                          Kente Design
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

                  {/* Wishlist button */}
                  <div className="absolute top-3 right-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-10 w-10 p-0 bg-white/90 hover:bg-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
                    >
                      <Heart className="h-4 w-4 hover:fill-red-500 hover:text-red-500 transition-colors duration-200" />
                    </Button>
                  </div>

                  {/* Stock badges */}
                  {item.stock <= 5 && item.stock > 0 && (
                    <div className="absolute top-3 left-3">
                      <Badge
                        variant="destructive"
                        className="text-xs font-semibold"
                      >
                        Only {item.stock} left
                      </Badge>
                    </div>
                  )}
                  {item.stock === 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Badge
                        variant="secondary"
                        className="text-sm font-semibold bg-white text-gray-900"
                      >
                        Out of Stock
                      </Badge>
                    </div>
                  )}
                </div>

                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs font-medium">
                        {item.category}
                      </Badge>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className="h-3 w-3 fill-yellow-400 text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>

                    <h4 className="font-semibold text-xl leading-tight">
                      {item.name}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>

                    <div className="flex items-center justify-between pt-2">
                      <div>
                        <span
                          className="font-bold text-2xl"
                          style={{ color: storeData.accentColor }}
                        >
                          ₵{item.price}
                        </span>
                        {item.stock > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {item.stock} in stock
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        disabled={item.stock === 0}
                        className="px-6 py-2 font-semibold transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: storeData.primaryColor,
                          color: "white",
                          border: "none",
                        }}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {item.stock === 0 ? "Out of Stock" : "Add to Cart"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* View All Products Button */}
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="px-8 py-4 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
              style={{
                backgroundColor: storeData.primaryColor,
                color: "white",
                border: "none",
              }}
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              View All Products
            </Button>
          </div>
        </div>

        {/* About Section */}
        <div className="p-12 bg-white">
          <div className="max-w-6xl mx-auto">
            {/* Main About Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3
                  className="text-4xl font-bold mb-6"
                  style={{ color: storeData.primaryColor }}
                >
                  Our Story
                </h3>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Kente is more than just a fabric; it's a symbol of our rich
                  cultural heritage. Each pattern tells a story, and every color
                  has meaning. Our master weavers use traditional techniques
                  passed down through generations to create these beautiful
                  pieces.
                </p>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  Founded with a passion for preserving and sharing Ghana's
                  textile traditions, we work directly with skilled artisans to
                  bring you authentic Kente designs that honor our ancestors
                  while embracing modern craftsmanship.
                </p>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      15+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Years Experience
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      500+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Happy Customers
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      100%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Authentic
                    </div>
                  </div>
                </div>

                {/* Learn More Button */}
                <div className="mt-8">
                  <Button
                    size="lg"
                    variant="outline"
                    className="px-8 py-4 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                    style={{
                      borderColor: storeData.primaryColor,
                      color: storeData.primaryColor,
                    }}
                    onClick={() => {
                      document
                        .getElementById("contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <Award className="h-5 w-5 mr-2" />
                    Learn More About Us
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <div
                      className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: storeData.accentColor }}
                    >
                      <span className="text-6xl">🧵</span>
                    </div>
                    <p
                      className="text-lg font-semibold"
                      style={{ color: storeData.primaryColor }}
                    >
                      Handcrafted with Love
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Award
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">
                  Authentic Designs
                </h4>
                <p className="text-muted-foreground">
                  Traditional patterns with deep cultural significance, each
                  telling a unique story
                </p>
              </div>
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Users
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">Master Weavers</h4>
                <p className="text-muted-foreground">
                  Skilled artisans with decades of experience in traditional
                  weaving techniques
                </p>
              </div>
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Shield
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">
                  Quality Guaranteed
                </h4>
                <p className="text-muted-foreground">
                  Premium materials and meticulous craftsmanship in every piece
                  we create
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2
                  className="text-4xl font-bold mb-6"
                  style={{ color: storeData.primaryColor }}
                >
                  Get in Touch
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Have questions about our Kente designs? We'd love to hear from
                  you. Reach out to us for custom orders, wholesale inquiries,
                  or any questions.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Contact Information */}
                <div className="space-y-8">
                  <div>
                    <h3
                      className="text-2xl font-semibold mb-6"
                      style={{ color: storeData.primaryColor }}
                    >
                      Contact Information
                    </h3>
                    <div className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: storeData.accentColor }}
                        >
                          <Mail
                            className="h-6 w-6"
                            style={{ color: storeData.primaryColor }}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg mb-1">Email</h4>
                          <p className="text-muted-foreground">
                            {storeData.contactInfo?.email ||
                              "info@kentestore.com"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            We respond within 24 hours
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: storeData.accentColor }}
                        >
                          <Phone
                            className="h-6 w-6"
                            style={{ color: storeData.primaryColor }}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg mb-1">Phone</h4>
                          <p className="text-muted-foreground">
                            {storeData.contactInfo?.phone || "+233 24 123 4567"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Mon-Fri, 9AM-6PM GMT
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: storeData.accentColor }}
                        >
                          <MapPin
                            className="h-6 w-6"
                            style={{ color: storeData.primaryColor }}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg mb-1">
                            Location
                          </h4>
                          <p className="text-muted-foreground">
                            {storeData.contactInfo?.location || "Accra, Ghana"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Visit our workshop by appointment
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="bg-white p-6 rounded-xl border border-gray-100">
                    <h4
                      className="font-semibold text-lg mb-4"
                      style={{ color: storeData.primaryColor }}
                    >
                      Business Hours
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Monday - Friday
                        </span>
                        <span className="font-medium">
                          {storeData.contactInfo?.businessHours?.weekdays ||
                            "9:00 AM - 6:00 PM"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Saturday</span>
                        <span className="font-medium">
                          {storeData.contactInfo?.businessHours?.saturday ||
                            "10:00 AM - 4:00 PM"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sunday</span>
                        <span className="font-medium">
                          {storeData.contactInfo?.businessHours?.sunday ||
                            "Closed"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-white p-8 rounded-xl border border-gray-100">
                  <h3
                    className="text-2xl font-semibold mb-6"
                    style={{ color: storeData.primaryColor }}
                  >
                    Send us a Message
                  </h3>
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          placeholder="Your first name"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          placeholder="Your last name"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        placeholder="What can we help you with?"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Message
                      </label>
                      <textarea
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Tell us about your inquiry..."
                      />
                    </div>
                    <Button
                      className="w-full transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                      style={{
                        backgroundColor: storeData.primaryColor,
                        color: "white",
                        border: "none",
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  </form>

                  {/* Get Started Button */}
                  <div className="mt-6 text-center">
                    <Button
                      size="lg"
                      variant="outline"
                      className="px-8 py-4 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                      style={{
                        borderColor: storeData.accentColor,
                        color: storeData.accentColor,
                      }}
                      onClick={() => {
                        document
                          .getElementById("store")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Start Shopping Now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="py-8 border-t"
          style={{ backgroundColor: storeData.primaryColor, color: "white" }}
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Store Info */}
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  {storeData.logo && (
                    <img
                      src={storeData.logo}
                      alt="Store Logo"
                      className="h-16 w-16 object-contain bg-white rounded-full p-3 shadow-lg"
                    />
                  )}
                  <div>
                    <h3 className="text-2xl font-bold">
                      {storeData.name || "My Kente Store"}
                    </h3>
                    <p className="text-sm opacity-90">
                      Authentic Kente Designs
                    </p>
                  </div>
                </div>
                <p className="text-sm opacity-90 mb-4 max-w-lg leading-relaxed">
                  {storeData.description ||
                    "Discover authentic Kente designs that celebrate our rich cultural heritage. Each piece is carefully crafted with traditional techniques passed down through generations."}
                </p>
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-white border-white bg-white/10 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg backdrop-blur-sm"
                    onClick={() => {
                      document
                        .getElementById("contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email Us
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-white border-white bg-white/10 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg backdrop-blur-sm"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Us
                  </Button>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
                <div className="space-y-3">
                  <a
                    href="#home"
                    className="block text-sm opacity-90 hover:opacity-100 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("home")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Home
                  </a>
                  <a
                    href="#about"
                    className="block text-sm opacity-90 hover:opacity-100 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("about")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    About Us
                  </a>
                  <a
                    href="#store"
                    className="block text-sm opacity-90 hover:opacity-100 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("store")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Our Store
                  </a>
                  <a
                    href="#contact"
                    className="block text-sm opacity-90 hover:opacity-100 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Contact Us
                  </a>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact Info</h4>
                <div className="space-y-4 text-sm">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: storeData.accentColor }}
                    >
                      <Mail
                        className="h-4 w-4"
                        style={{ color: storeData.primaryColor }}
                      />
                    </div>
                    <div>
                      <p className="opacity-90 font-medium">
                        {storeData.contactInfo?.email || "info@kentestore.com"}
                      </p>
                      <p className="opacity-70 text-xs">
                        We respond within 24 hours
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: storeData.accentColor }}
                    >
                      <Phone
                        className="h-4 w-4"
                        style={{ color: storeData.primaryColor }}
                      />
                    </div>
                    <div>
                      <p className="opacity-90 font-medium">
                        {storeData.contactInfo?.phone || "+233 24 123 4567"}
                      </p>
                      <p className="opacity-70 text-xs">Mon-Fri, 9AM-6PM GMT</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: storeData.accentColor }}
                    >
                      <MapPin
                        className="h-4 w-4"
                        style={{ color: storeData.primaryColor }}
                      />
                    </div>
                    <div>
                      <p className="opacity-90 font-medium">
                        {storeData.contactInfo?.location || "Accra, Ghana"}
                      </p>
                      <p className="opacity-70 text-xs">Visit by appointment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/20 pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="text-sm opacity-90 mb-4 md:mb-0">
                  © 2025 {storeData.name || "My Kente Store"}. All rights
                  reserved.
                </div>
                <div className="flex items-center gap-6 text-sm opacity-90">
                  <a
                    href="#"
                    className="hover:opacity-100 transition-opacity hover:underline"
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="#"
                    className="hover:opacity-100 transition-opacity hover:underline"
                  >
                    Terms of Service
                  </a>
                  <a
                    href="#"
                    className="hover:opacity-100 transition-opacity hover:underline"
                  >
                    Shipping Info
                  </a>
                  <span className="opacity-70">
                    Powered by Kente Weave Design
                  </span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StorePreview;
